import { BaseEvent, ErrorInfo, ResourceErrorInfo, EventIds } from '@fpkg/stats-core';

export const countDOM = (rootNode: Element) => {
  let count = 0;
  if (rootNode.children && rootNode.children.length) {
    for (let i = 0; i < rootNode.children.length; i++) {
      if (rootNode.children[i].nodeType === Node.TEXT_NODE) {
        count++;
      } else if (rootNode.children[i].nodeType === Node.ELEMENT_NODE) {
        count++;
        count += countDOM(rootNode.children[i]);
      }
    }
  }
  return count;
};

/**
 * 手动检测页面是否白屏
 * 默认如果 rootSelector 元素下子元素数量小于 threshold 个，则判断为白屏
 * @param rootSelector 计算白屏的dom根节点，支持querySelector语法, 如果不填，判断逻辑为 #app->#root->body。
 *                     手动配置rootSelector为打分的根节点，准确度才更高。
 * @param threshold 白屏的dom阈值,如果小于阈值,则认为可能是白屏。
 * @param maxTime 频率，单位/毫秒，多少分钟内判断一次，默认为120000，如果为0每次都判断
 */
export type DetectBlankScreenOpts = {
  root?: HTMLElement;
  rootSelector?: string;
  threshold?: number;
  maxTime?: number;
};
export const detectBlankScreen = ({
  rootSelector = '',
  threshold = 1.5,
  maxTime = 120000,
  root,
}: DetectBlankScreenOpts = {}): {
  isBlankScreen: boolean;
  domCount: number;
} => {
  const nowTime = new Date().getTime();
  let lastBlankScreenTime = 0;
  try {
    lastBlankScreenTime = Number(localStorage.getItem('stats_last_blank_screen_time'));
  } catch (e) {
    console.error(e);
  }
  if (maxTime > 0 && nowTime - lastBlankScreenTime < maxTime) {
    // maxTime内只上报一次
    return {
      isBlankScreen: false,
      domCount: 0,
    };
  }

  let rootNode: HTMLElement | null = null;
  if (root) {
    rootNode = root;
  } else if (!rootSelector) {
    // 默认获取根节点逻辑
    rootNode = document.getElementById('app') || document.getElementById('root') || document.body;
  } else {
    rootNode = document.querySelector(rootSelector);
  }
  if (!rootNode) {
    // 触发白屏时保存时间
    localStorage.setItem('stats_last_blank_screen_time', `${nowTime}`);
    return {
      isBlankScreen: true,
      domCount: 0,
    };
  }

  const domCount = countDOM(rootNode);
  if (domCount < threshold!) {
    // 触发白屏时保存时间
    localStorage.setItem('stats_last_blank_screen_time', `${nowTime}`);
    return {
      isBlankScreen: true,
      domCount,
    };
  }
  return {
    isBlankScreen: false,
    domCount,
  };
};

export function detectBlankScreenOnJSError(
  reportInfo: ErrorInfo,
  detectOpts: DetectBlankScreenOpts,
  handlerBlankScreen: Function
) {
  const evenInfo: any = {
    ...reportInfo,
    $url: window.location.href, // 保存当前页面路径
    $title: document.title, // 保存当前页面title
  };
  setTimeout(() => {
    const { isBlankScreen, domCount } = detectBlankScreen(detectOpts);
    if (isBlankScreen) {
      evenInfo.dom_count = domCount;
      handlerBlankScreen(evenInfo);
    }
  }, 5000);
}

export function detectBlankScreenOnResourceError(
  reportInfo: ResourceErrorInfo,
  detectOpts: DetectBlankScreenOpts,
  handlerBlankScreen: Function
) {
  if (/.*\.js.*/gi.test(reportInfo.filename)) {
    // js资源加载异常，才去判断是否白屏

    const evenInfo: any = {
      ...reportInfo,
      $url: window.location.href, // 保存当前页面路径
      $title: document.title, // 保存当前页面title
    };

    setTimeout(() => {
      const { isBlankScreen, domCount } = detectBlankScreen(detectOpts);
      if (isBlankScreen) {
        evenInfo.dom_count = domCount;
        handlerBlankScreen(evenInfo);
      }
    }, 5000);
  }
}

export function detectBlankScreenOnError(
  reportInfo: BaseEvent,
  detectOpts: DetectBlankScreenOpts,
  handlerBlankScreen: Function
) {
  try {
    if (reportInfo && reportInfo.event === EventIds.ResourceErrorInfo) {
      detectBlankScreenOnResourceError(reportInfo as ResourceErrorInfo, detectOpts, handlerBlankScreen);
    } else if (reportInfo && reportInfo.event === EventIds.ErrorInfo) {
      detectBlankScreenOnJSError(reportInfo as ErrorInfo, detectOpts, handlerBlankScreen);
    } else {
      console.error('只支持在发生js异常或资源加载异常时检测是否白屏，此次监测到的异常信息为：', reportInfo);
    }
  } catch (e) {
    console.error('白屏检测发生错误', e);
  }
}
